import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, ExternalLink, AlertCircle } from 'lucide-react';
import {
  fetchMySubscriptionsThunk,
  cancelSubscriptionThunk,
  fetchMonetizationConfigThunk,
} from '../../../redux/monetization/monetizationActionThunks';
import {
  selectMySubscriptions,
  selectMonetizationConfig,
  selectMonetizationConfigLoaded,
  selectMonetizationLoading,
  selectMonetizationError,
} from '../../../redux/monetization/monetizationSlice';

const statusStyles = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  canceled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  past_due: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
};

const SubscriptionsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subscriptions = useSelector(selectMySubscriptions);
  const config = useSelector(selectMonetizationConfig);
  const configLoaded = useSelector(selectMonetizationConfigLoaded);
  const loading = useSelector(selectMonetizationLoading);
  const error = useSelector(selectMonetizationError);

  useEffect(() => {
    if (!configLoaded) dispatch(fetchMonetizationConfigThunk());
    if (subscriptions.length === 0) dispatch(fetchMySubscriptionsThunk());
  }, [dispatch, configLoaded, subscriptions.length]);

  const handleCancel = async (groupId) => {
    try {
      await dispatch(cancelSubscriptionThunk(groupId)).unwrap();
      dispatch(fetchMySubscriptionsThunk());
    } catch {
      // cancelSubscriptionThunk.rejected already sets state.monetization.error.
      // The unconditional refetch below used to fire regardless of outcome,
      // and fetchMySubscriptionsThunk.pending resets that same error field
      // to null -- wiping the failure message before the user could see it.
      // Only refetch on confirmed success now.
    }
  };

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings.subscriptions.pageTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('settings.subscriptions.pageSubtitle')}
        </p>
      </div>

      {!config.monetization_live && (
        <div className="max-w-3xl mb-6 flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600/30 rounded-lg">
          <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('settings.subscriptions.testModeNotice')}
          </p>
        </div>
      )}

      {error && (
        <div className="max-w-3xl mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="max-w-3xl">
        {subscriptions.length === 0 ? (
          <div className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 rounded-2xl p-10 text-center">
            <CreditCard className="mx-auto text-gray-400 dark:text-gray-600 mb-3" size={32} />
            <p className="text-gray-600 dark:text-gray-400">{t('settings.subscriptions.emptyState')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 rounded-2xl p-5 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/groups/${sub.group_id}`)}
                      className="text-gray-900 dark:text-white font-semibold hover:underline flex items-center gap-1 truncate"
                    >
                      {sub.group_name}
                      <ExternalLink size={13} />
                    </button>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusStyles[sub.status] || statusStyles.expired}`}>
                      {t(`settings.subscriptions.statusLabels.${sub.status}`, { defaultValue: sub.status.replace('_', ' ') })}
                    </span>
                  </div>
                  {sub.current_period_end && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {sub.status === 'active' ? t('settings.subscriptions.renews') : t('settings.subscriptions.accessEnds')}{' '}
                      {t('settings.subscriptions.onDate', { date: new Date(sub.current_period_end).toLocaleDateString() })}
                    </p>
                  )}
                </div>
                {sub.status === 'active' && (
                  <button
                    onClick={() => handleCancel(sub.group_id)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-600/20 dark:hover:bg-red-600/30 border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-300 rounded-xl text-sm font-medium disabled:opacity-50 flex-shrink-0"
                  >
                    {t('settings.subscriptions.cancel')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionsPage;
